import type { ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { Palette } from "@jackedlogs/shared";
import { DETAIL_PANE_MAX_WIDTH } from "@jackedlogs/ui";

import { MaxesScreen } from "../MaxesScreen";
import { MeTargetsScreen } from "../MeTargetsScreen";
import { EffortScreen } from "../EffortScreen";
import { TimersScreen } from "../TimersScreen";
import { MeasurementsScreen } from "../MeasurementsScreen";
import type { MeKey } from "./meNav";

// The Me content pane (ME-1): the selected Me section's screen. The account
// menu closes on select and no tab names the section, so this adds the heading
// + "back to Me" affordance the tab bar would otherwise supply. Form screens
// are width-capped on wide like the Settings detail pane; Maxes is itself
// master-detail and fills the pane; Goals owns its own EditorTopBar (back +
// title + Save) so it mounts bare. Extracted from AppShell to keep the shell
// under the size cap and to isolate the Me axis.
export function MeContentPane({
  meSub,
  wide,
  p,
  onBack,
}: {
  meSub: MeKey;
  wide: boolean;
  p: Palette;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(p);

  const screen = (key: MeKey): ReactNode => {
    switch (key) {
      case "targets":
        return <MeTargetsScreen onClose={onBack} />;
      case "corpo":
        return <MeasurementsScreen />;
      case "maxes":
        return <MaxesScreen />;
      case "effort":
        return <EffortScreen />;
      case "timers":
        return <TimersScreen />;
    }
  };
  // targets is excluded: it owns its title in the editor's EditorTopBar and never
  // reaches the heading below (short-circuited before this is called).
  const title = (key: Exclude<MeKey, "targets">): string => {
    switch (key) {
      case "corpo":
        return t("mobile.body.measurementsTitle", { defaultValue: "Measurements" });
      case "maxes":
        return t("mobile.profile.navMaxes", { defaultValue: "MAXes" });
      case "effort":
        return t("mobile.settings.navEffort", { defaultValue: "Effort settings" });
      case "timers":
        return t("mobile.profile.navRestTimer", { defaultValue: "Timers" });
    }
  };

  // Goals owns its full chrome (back + title + Save in the editor's own top
  // bar), so it mounts bare — no heading (that would double the back+title) and
  // no width-cap (the editor is itself responsive). Its own back calls onBack.
  if (meSub === "targets") return <>{screen(meSub)}</>;

  return (
    <View style={styles.meWrap}>
      <View style={styles.meHead}>
        <Pressable
          onPress={onBack}
          accessibilityRole="button"
          accessibilityLabel={t("mobile.common.back", { defaultValue: "Back" })}
          hitSlop={8}
          style={({ pressed }) => [styles.meBack, pressed && styles.meBackPressed]}
        >
          <Text style={styles.meBackLabel}>
            ‹ {t("mobile.common.back", { defaultValue: "Back" })}
          </Text>
        </Pressable>
        <Text style={styles.meTitle}>{title(meSub)}</Text>
      </View>
      {wide && meSub !== "maxes" ? (
        <View style={styles.meFormCap}>{screen(meSub)}</View>
      ) : (
        screen(meSub)
      )}
    </View>
  );
}

function makeStyles(p: Palette) {
  return StyleSheet.create({
    meWrap: { flex: 1 },
    meHead: {
      flexDirection: "row",
      alignItems: "baseline",
      gap: 12,
      paddingHorizontal: 4,
      paddingBottom: 8,
    },
    meBack: { minHeight: 44, justifyContent: "center" },
    meBackPressed: { opacity: 0.6 },
    meBackLabel: { color: p.brand500, fontSize: 14, fontWeight: "600" },
    meTitle: { color: p.ink, fontSize: 20, fontWeight: "700" },
    meFormCap: { flex: 1, width: "100%", maxWidth: DETAIL_PANE_MAX_WIDTH },
  });
}
